import React from "react";
import { 
  Search, 
  Map as MapIcon, 
  Navigation, 
  Layers, 
  CheckCircle, 
  Users, 
  Target, 
  GripVertical, 
  Info,
  ChevronRight,
  Maximize
} from "lucide-react";

// ==========================================
// --- MINI MOCKUP COMPONENTS ---
// ==========================================

const MapLogicMockup = () => (
  <div className="relative w-full h-56 bg-slate-200 rounded-2xl overflow-hidden border border-slate-300 shadow-inner group">
    {/* Simulated Map Background */}
    <div className="absolute inset-0 flex items-center justify-center bg-[url('https://api.mapbox.com/styles/v1/mapbox/light-v10/static/-74.006,40.7128,10,0/600x400?access_token=YOUR_TOKEN')] bg-cover bg-center opacity-60">
      {/* Search Radius Ring */}
      <div className="w-40 h-40 bg-blue-500/20 border-2 border-blue-500 rounded-full animate-pulse flex items-center justify-center">
        <div className="w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-xl"></div>
      </div>
      {/* Result Markers */}
      <div className="absolute top-12 right-24 w-3 h-3 bg-red-500 rounded-full border border-white shadow-sm"></div>
      <div className="absolute bottom-16 left-32 w-3 h-3 bg-red-500 rounded-full border border-white shadow-sm"></div>
    </div>
    
    {/* Map Overlay Stats */}
    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Radius</p>
      <p className="text-xs font-bold text-gray-900">15 Miles</p>
    </div>

    <div className="absolute bottom-4 right-4 bg-blue-600 text-white p-2 rounded-lg shadow-lg">
      <Maximize size={14} />
    </div>
  </div>
);

const ProgramCardMockup = () => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
    <GripVertical size={16} className="text-gray-300" />
    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-black">HS</div>
    <div className="flex-1">
      <h4 className="text-sm font-bold text-gray-900">Westside Varsity</h4>
      <p className="text-[10px] text-gray-500 font-medium italic">Basketball • 4.2 miles away</p>
    </div>
    <button className="px-3 py-1 bg-gray-900 text-white text-[10px] font-bold rounded-md">Following</button>
  </div>
);

// ==========================================
// --- MAIN PAGE COMPONENT ---
// ==========================================

const MarketplacePage = () => {
  return (
    <div className="space-y-12 pb-20">
      {/* Header Section */}
      <section>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Program Marketplace</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          The Marketplace is where discovery happens. Connect with high schools, clubs, 
          and training academies to see their standards and join their recruitment pipelines.
        </p>
      </section>

      {/* 1. Map Discovery & FitBounds */}
      <section className="grid md:grid-cols-2 gap-12 items-center">
        <MapLogicMockup />
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><MapIcon size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Adaptive Map Discovery</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Nuvio Sport uses an intelligent <strong>FitBounds</strong> engine to help you find programs within your range. 
            The map view automatically scales based on your search radius.
          </p>
          
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3 shadow-sm">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Navigation size={12} /> Auto-Zoom Logic
            </h4>
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <div className="p-2 bg-gray-50 rounded">Radius ≤ 5mi: <span className="text-blue-600">Zoom 12</span></div>
              <div className="p-2 bg-gray-50 rounded">Radius ≤ 15mi: <span className="text-blue-600">Zoom 11</span></div>
              <div className="p-2 bg-gray-50 rounded">Radius ≤ 30mi: <span className="text-blue-600">Zoom 10</span></div>
              <div className="p-2 bg-gray-50 rounded">Radius &gt; 30mi: <span className="text-blue-600">Zoom 9</span></div>
            </div>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* 2. The Following System & Outcomes */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="bg-orange-100 p-2 rounded-lg text-orange-600"><Users size={20}/></div>
              <h3 className="text-xl font-bold text-gray-900">Following & Priority</h3>
            </div>
            <p className="text-gray-600 text-sm">Following a program is more than just a social connection.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <ProgramCardMockup />
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
              <Info className="text-blue-600 flex-shrink-0" size={18} />
              <p className="text-xs text-blue-800 leading-relaxed">
                <strong>Outcome:</strong> When you follow a program, your profile appears in the coach's 
                "Prospect Radar." This allows them to monitor your combine growth before you even meet.
              </p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-6">
            <h4 className="font-bold text-gray-900 flex items-center gap-2">
              <Target className="text-red-500" size={18} /> Recruitment Pipeline
            </h4>
            <div className="space-y-4">
              {[
                { title: "Standard Benchmarks", desc: "Unlock the program's target scores for your position." },
                { title: "Priority Feed", desc: "Rank programs to see their news and updates first." },
                { title: "Direct Pipeline", desc: "Allows coaches to send you direct roster invitations." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    <CheckCircle size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Outcomes & Analytics Section */}
      <section className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 text-white">
        <div className="max-w-xl space-y-4">
          <h3 className="text-2xl font-bold">Connecting Local Talent to Global Standards</h3>
          <p className="text-blue-100 text-sm leading-relaxed opacity-90">
            By searching through the Marketplace and following elite programs, Nuvio Sport 
            benchmarks your current metrics against the requirements of your target school. 
            This data is fed directly into your <strong>AI Training Plan</strong> to close the gap 
            between where you are and where you need to be.
          </p>
          <div className="pt-4 flex gap-4">
            <div className="px-4 py-2 bg-white/10 rounded-lg border border-white/20 text-[10px] font-bold uppercase tracking-widest">
              Location Verified
            </div>
            <div className="px-4 py-2 bg-white/10 rounded-lg border border-white/20 text-[10px] font-bold uppercase tracking-widest">
              Program Verified
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketplacePage;